import type { DisplayPointOfSaleInfos_PointOfSaleFragment } from '@gql-generated/graphql'
import { atom } from 'jotai'

// #region PointOfSaleDrawer

type PointOfSaleDrawerAtom = {
  pointOfSaleId?: DisplayPointOfSaleInfos_PointOfSaleFragment['id']
  changeStoreAllowed: boolean
  isOpen: boolean
}

export const pointOfSaleDrawerAtom = atom<PointOfSaleDrawerAtom>({
  pointOfSaleId: undefined,
  changeStoreAllowed: false,
  isOpen: false,
})

export const closePointOfSaleDrawerAtom = atom(null, (_get, set) => {
  set(pointOfSaleDrawerAtom, (prev) => ({ ...prev, isOpen: false }))
})

export const openPointOfSaleDrawerAtom = atom(
  null,
  (
    _get,
    set,
    { pointOfSaleId, changeStoreAllowed }: StrictOmit<PointOfSaleDrawerAtom, 'isOpen'>,
  ) => {
    set(pointOfSaleDrawerAtom, (prev) => ({
      ...prev,
      pointOfSaleId,
      changeStoreAllowed,
      isOpen: true,
    }))
  },
)

export const updateOpenPointOfSaleIdAtom = atom(null, (_get, set, pointOfSaleId: string) => {
  set(pointOfSaleDrawerAtom, (prev) => (prev.isOpen ? { ...prev, pointOfSaleId } : prev))
})

// #endregion PointOfSaleDrawer

// #region SelectProductPickupPointOfSaleDrawer

type SelectProductPickupPointOfSaleDrawerState =
  | {
      isOpen: true
      reference: string
      onClose?: (reference: string) => void
    }
  | {
      isOpen: false
      reference?: never
      onClose?: never
    }

export const __selectProductPickupPointOfSaleDrawerAtom =
  atom<SelectProductPickupPointOfSaleDrawerState>({
    isOpen: false,
    reference: undefined,
    onClose: undefined,
  })

export const __isSelectProductPickupPointOfSaleDrawerOpenAtom = atom(
  (get) => get(__selectProductPickupPointOfSaleDrawerAtom).isOpen,
  (_get, set, isOpen: boolean) => {
    if (!isOpen) {
      // closing the modal → reset
      set(__selectProductPickupPointOfSaleDrawerAtom, { isOpen: false })
    }
  },
)

export const __closeSelectProductPickupPointOfSaleDrawerAtom = atom(null, (_get, set) => {
  const currentState = _get(__selectProductPickupPointOfSaleDrawerAtom)

  if (currentState.isOpen && currentState.onClose) {
    currentState.onClose(currentState.reference)
  }

  set(__selectProductPickupPointOfSaleDrawerAtom, { isOpen: false, onClose: undefined })
})

/**
 * @example
 * const openSelectProductPickupPointOfSaleDrawer = useSetAtom(openSelectProductPickupPointOfSaleDrawerAtom)
 */
export const openSelectProductPickupPointOfSaleDrawerAtom = atom(
  null,
  (_get, set, params: { reference: string; onClose?: (reference: string) => void }) => {
    set(__selectProductPickupPointOfSaleDrawerAtom, (old) => ({
      ...old,
      isOpen: true,
      reference: params.reference,
      onClose: params.onClose,
    }))
  },
)

// #endregion SelectProductPickupPointOfSaleDrawer

// #region SelectPickupPointOfSaleDrawer

export const __selectPickupPointOfSaleDrawerAtom = atom<{
  isOpen: boolean
  selectedId: string | undefined
  onClose?: (selectedId: string) => void
}>({
  isOpen: false,
  selectedId: undefined,
  onClose: undefined,
})

export const __isSelectPickupPointOfSaleDrawerOpenAtom = atom(
  (get) => get(__selectPickupPointOfSaleDrawerAtom).isOpen,
  (_get, set, isOpen: boolean) => {
    if (!isOpen) {
      set(__selectPickupPointOfSaleDrawerAtom, (old) => ({ ...old, isOpen: false }))
    }
  },
)

export const __closeSelectPickupPointOfSaleDrawerAtom = atom(null, (_get, set) => {
  set(__selectPickupPointOfSaleDrawerAtom, (old) => ({ ...old, isOpen: false }))
})

/**
 * @example
 * const openSelectPickupPointOfSaleDrawer = useSetAtom(openSelectPickupPointOfSaleDrawerAtom)
 */
export const openSelectPickupPointOfSaleDrawerAtom = atom(
  __selectPickupPointOfSaleDrawerAtom,
  (
    _get,
    set,
    options: string | { selectedId: string; onClose?: (selectedPointOfSaleId: string) => void },
  ) => {
    set(__selectPickupPointOfSaleDrawerAtom, (old) => {
      return {
        ...old,
        isOpen: true,
        selectedId: typeof options === 'string' ? options : options.selectedId,
        onClose: typeof options === 'string' ? old.onClose : options.onClose,
      }
    })
  },
)

// #endregion SelectPickupPointOfSaleDrawer

// #region SelectShippingPickupPointOfSaleDrawer

type SelectShippingPickupPointOfSaleDrawerAtom = {
  isOpen: boolean
  forceStorePickup: boolean
  onClose?: (pointOfSaleId: string) => void
}

export const __selectShippingPickupPointOfSaleDrawerAtom =
  atom<SelectShippingPickupPointOfSaleDrawerAtom>({
    isOpen: false,
    forceStorePickup: false,
    onClose: undefined,
  })

const closedSelectShippingPickupPointOfSaleDrawer = (
  old: SelectShippingPickupPointOfSaleDrawerAtom,
): SelectShippingPickupPointOfSaleDrawerAtom => ({ ...old, isOpen: false, onClose: undefined })

export const __isSelectShippingPickupPointOfSaleDrawerOpenAtom = atom(
  (get) => get(__selectShippingPickupPointOfSaleDrawerAtom).isOpen,
  (_get, set, isOpen: boolean) =>
    set(__selectShippingPickupPointOfSaleDrawerAtom, (old) =>
      isOpen ? { ...old, isOpen } : closedSelectShippingPickupPointOfSaleDrawer(old),
    ),
)

export const __closeSelectShippingPickupPointOfSaleDrawerAtom = atom(null, (get, set) => {
  const { isOpen } = get(__selectShippingPickupPointOfSaleDrawerAtom)

  if (isOpen) {
    set(__selectShippingPickupPointOfSaleDrawerAtom, closedSelectShippingPickupPointOfSaleDrawer)
  }
})

type OpenSelectShippingPickupPointOfSaleDrawerParams = StrictOmit<
  SelectShippingPickupPointOfSaleDrawerAtom,
  'isOpen'
>

/**
 * To open the drawer, use this atom
 *
 * @example
 * const openSelectShippingPickupPointOfSaleDrawer = useSetAtom(openSelectShippingPickupPointOfSaleDrawerAtom)
 * openSelectShippingPickupPointOfSaleDrawer({ forceStorePickup: true, onClose: () => alert('Closed!') })
 */
export const openSelectShippingPickupPointOfSaleDrawerAtom = atom(
  null,
  (_, set, { onClose, forceStorePickup }: OpenSelectShippingPickupPointOfSaleDrawerParams) => {
    set(__selectShippingPickupPointOfSaleDrawerAtom, (old) => ({
      ...old,
      isOpen: true,
      forceStorePickup,
      onClose,
    }))
  },
)

// #endregion SelectShippingPickupPointOfSaleDrawer

// #region SelectFavoritePointOfSaleDrawer

export const __selectFavoritePointOfSaleDrawerAtom = atom<{
  isOpen: boolean
  selectedId: string | undefined
  previousPointOfSaleId: string | undefined
  onClose?: (newPointOfSaleId: string) => void
}>({
  isOpen: false,
  selectedId: undefined,
  previousPointOfSaleId: undefined,
  onClose: undefined,
})

export const __isSelectFavoritePointOfSaleDrawerOpenAtom = atom(
  (get) => get(__selectFavoritePointOfSaleDrawerAtom).isOpen,
  (_get, set, isOpen: boolean) => {
    if (!isOpen) {
      // closing the modal → reset
      set(__selectFavoritePointOfSaleDrawerAtom, (old) => ({ ...old, isOpen: false }))
    }
  },
)

export const __closeSelectFavoritePointOfSaleDrawerAtom = atom(
  null,
  (_get, set, newPointOfSaleId: string) => {
    const currentState = _get(__selectFavoritePointOfSaleDrawerAtom)

    if (currentState.isOpen && currentState.onClose) {
      currentState.onClose(newPointOfSaleId)
    }

    set(__selectFavoritePointOfSaleDrawerAtom, (old) => ({ ...old, isOpen: false }))
  },
)

/**
 * To open the drawer, set the point of sale id
 * @example
 * const openSelectFavoritePointOfSaleDrawer = useSetAtom(openSelectFavoritePointOfSaleDrawerAtom)
 */
export const openSelectFavoritePointOfSaleDrawerAtom = atom(
  __selectFavoritePointOfSaleDrawerAtom,
  (
    _get,
    set,
    {
      selectedId,
      previousPointOfSaleId,
      onClose,
    }: {
      selectedId: string
      previousPointOfSaleId?: string
      onClose?: (newPointOfSaleId: string) => void
    },
  ) => {
    set(__selectFavoritePointOfSaleDrawerAtom, {
      isOpen: true,
      selectedId,
      previousPointOfSaleId,
      onClose,
    })
  },
)

// #endregion SelectFavoritePointOfSaleDrawer
